// src/lib/pushNotifications.js
// Web Push + home-screen badge client helpers (Notifications Commit 2).
//
// Pairs with:
//   - public/sw.js               — 'push' / 'notificationclick' handlers
//   - supabase/functions/send-push — VAPID-signs and sends
//   - push_subscriptions table    — one row per device endpoint
//
import { supabaseFetch } from './supabase';

// VAPID public key — the NON-SECRET half of the VAPID keypair. It ships to every browser anyway
// (pushManager.subscribe sends it to the push service). It is a build variable,
// REACT_APP_VAPID_PUBLIC_KEY, because each instance has its own keypair (Roadmap #97; brief
// docs/briefs/2026-10-15-instance-config.md, B4, ratified by Michael 16 Oct 2026). A production
// build without it fails (scripts/instance-config.js check); in development enablePush() below
// refuses with a console error.
//
// The PRIVATE half lives ONLY in Supabase Edge Function secrets (VAPID_PRIVATE_KEY, beside
// VAPID_PUBLIC_KEY) and signs every push. These are a MATCHED PAIR: the build variable must equal
// the VAPID_PUBLIC_KEY secret, and regenerating the keypair invalidates every existing
// push_subscriptions row, so all devices must re-subscribe. See Architecture.md § Notifications —
// Send Path.
export const VAPID_PUBLIC_KEY = (process.env.REACT_APP_VAPID_PUBLIC_KEY || '').trim();

// VAPID keys are URL-safe base64; pushManager.subscribe wants a Uint8Array.
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
  const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
  const raw = window.atob(base64);
  const output = new Uint8Array(raw.length);
  for (let i = 0; i < raw.length; i++) output[i] = raw.charCodeAt(i);
  return output;
}

// Full Web Push support requires SW + PushManager + Notification.
export function isPushSupported() {
  return (
    typeof navigator !== 'undefined' &&
    'serviceWorker' in navigator &&
    typeof window !== 'undefined' &&
    'PushManager' in window &&
    'Notification' in window
  );
}

// iOS/iPadOS detection (incl. iPadOS reporting as Mac with touch).
export function isIos() {
  if (typeof navigator === 'undefined') return false;
  const ua = navigator.userAgent || '';
  const iOSDevice = /iPad|iPhone|iPod/.test(ua);
  const iPadOS = navigator.platform === 'MacIntel' && (navigator.maxTouchPoints || 0) > 1;
  return iOSDevice || iPadOS;
}

// Running as an installed PWA (standalone display mode).
export function isInStandaloneMode() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia?.('(display-mode: standalone)')?.matches ||
    window.navigator.standalone === true
  );
}

// iOS only exposes Web Push to an INSTALLED PWA. This flags the case where the
// opt-in cannot work until the user adds the site to their home screen.
export function isIosNeedsInstall() {
  return isIos() && !isInStandaloneMode();
}

// 'granted' | 'denied' | 'default' | 'unsupported'
export function getPushPermission() {
  if (!isPushSupported()) return 'unsupported';
  return Notification.permission;
}

// Upsert a browser subscription's row. Shared by enablePush (fresh opt-in) and
// checkPushSubscriptionStatus (self-heal of a browser subscription whose DB row
// went missing). Upsert on the unique endpoint — a returning device refreshes
// its row (needs the push_subs_update_own RLS policy). return=minimal (no read-back).
async function storeSubscription(userId, subscription) {
  const json = subscription.toJSON();
  const row = {
    user_id: userId,
    endpoint: subscription.endpoint,
    p256dh: json.keys?.p256dh,
    auth: json.keys?.auth,
    user_agent: navigator.userAgent,
    last_seen_at: new Date().toISOString(),
  };

  const { error } = await supabaseFetch.insert('push_subscriptions', row, {
    upsert: true,
    onConflict: 'endpoint',
    returnMinimal: true,
  });
  return !error;
}

/**
 * Opt in to Web Push: request permission → subscribe → upsert the subscription.
 * Deliberate user action only — never call this on load.
 * @param {string} userId - the logged-in user's id (MyProfile passes its userId prop)
 * @returns {Promise<{ok: boolean, reason: string}>}
 *   reason ∈ granted | denied | unsupported | ios-needs-install | no-key | no-user | error
 */
export async function enablePush(userId) {
  if (!userId) return { ok: false, reason: 'no-user' };
  if (!isPushSupported()) return { ok: false, reason: 'unsupported' };
  if (isIosNeedsInstall()) return { ok: false, reason: 'ios-needs-install' };
  if (!VAPID_PUBLIC_KEY) {
    // A production build cannot reach here (the build fails without the key); a
    // development run without REACT_APP_VAPID_PUBLIC_KEY does.
    console.error('enablePush: REACT_APP_VAPID_PUBLIC_KEY is not set');
    return { ok: false, reason: 'no-key' };
  }

  try {
    const permission = await Notification.requestPermission();
    if (permission !== 'granted') return { ok: false, reason: 'denied' };

    const registration = await navigator.serviceWorker.ready;

    // Reuse an existing subscription if present, else create one.
    let subscription = await registration.pushManager.getSubscription();
    if (!subscription) {
      subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(VAPID_PUBLIC_KEY),
      });
    }

    const stored = await storeSubscription(userId, subscription);
    if (!stored) {
      console.error('enablePush: failed to store subscription');
      return { ok: false, reason: 'error' };
    }

    return { ok: true, reason: 'granted' };
  } catch (err) {
    console.error('enablePush error:', err);
    return { ok: false, reason: 'error' };
  }
}

/**
 * Is push actually on? "On" means BOTH a browser subscription exists AND its
 * endpoint is confirmed present in push_subscriptions — permission alone proves
 * neither (see NotificationOptIn.jsx history: permission is granted before the
 * subscribe/store steps that can fail, so it alone false-positives "on").
 * If the browser has a subscription but the DB row is missing (subscribe
 * succeeded, store failed, or a previous session's write silently failed),
 * self-heals by re-storing rather than reporting a stale state.
 * @param {string} userId
 * @returns {Promise<{on: boolean, subscription: PushSubscription|null}>}
 */
export async function checkPushSubscriptionStatus(userId) {
  if (!userId || !isPushSupported() || isIosNeedsInstall()) {
    return { on: false, subscription: null };
  }

  try {
    // getRegistration (not .ready) — must not block the mount on a SW that may
    // never become active; no registration yet simply means "not on".
    const registration = await navigator.serviceWorker.getRegistration();
    if (!registration) return { on: false, subscription: null };

    const subscription = await registration.pushManager.getSubscription();
    if (!subscription) return { on: false, subscription: null };

    const { data, error } = await supabaseFetch.query('push_subscriptions', {
      select: 'id',
      eq: { endpoint: subscription.endpoint, user_id: userId },
      single: true,
    });
    if (!error && data) return { on: true, subscription };

    // Orphaned: browser subscription exists, DB row doesn't. Self-heal.
    const stored = await storeSubscription(userId, subscription);
    return { on: stored, subscription };
  } catch (err) {
    console.error('checkPushSubscriptionStatus error:', err);
    return { on: false, subscription: null };
  }
}

// ---- Home-screen app-icon badge (while the app is open) -------------------
// Mirrors the Commit 1 unread count. Guarded — not on all platforms/UAs.
export function updateAppBadge(count) {
  try {
    if (typeof navigator === 'undefined') return;
    if (count > 0 && typeof navigator.setAppBadge === 'function') {
      navigator.setAppBadge(count);
    } else if (typeof navigator.clearAppBadge === 'function') {
      navigator.clearAppBadge();
    }
  } catch (_) { /* non-fatal */ }
}

// ---- SW → client navigation bridge ----------------------------------------
// notificationclick posts { type:'navigate', hash } to a focused tab; set the
// URL hash so the existing App.js hashchange → handleHashNavigation path runs.
// Clearing first forces a re-fire when the target hash equals the current one
// (same trick the inbox row-click uses).
let navBridgeInstalled = false;
export function initServiceWorkerNavBridge() {
  if (navBridgeInstalled) return;
  if (typeof navigator === 'undefined' || !('serviceWorker' in navigator)) return;
  navBridgeInstalled = true;
  navigator.serviceWorker.addEventListener('message', (event) => {
    if (event.data?.type === 'navigate' && event.data.hash) {
      if (window.location.hash === event.data.hash) window.location.hash = '';
      window.location.hash = event.data.hash;
    }
  });
}
